<script setup>
import { onMounted, onBeforeUnmount, watch, ref, useSlots, shallowRef, markRaw } from 'vue'
import mapboxgl from 'mapbox-gl'

const props = defineProps({
  map: {
    type: Object,
    required: true
  },
  coordinates: {
    type: Array,
    required: false,
    default: () => []
  },
  offset: {
    type: [Number, Object, Array],
    required: false,
    default: 0
  },
  anchor: {
    type: String,
    required: false,
    default: 'bottom'
  },
  closeButton: {
    type: Boolean,
    required: false,
    default: true
  },
  closeOnClick: {
    type: Boolean,
    required: false,
    default: true
  },
  maxWidth: {
    type: String,
    required: false,
    default: '300px'
  },
  minWidth: {
    type: String,
    required: false,
    default: '300px'
  },
  className: {
    type: String,
    required: false,
    default: ''
  },
  show: {
    type: Boolean,
    required: false,
    default: false
  }
})

const emit = defineEmits(['close'])
const slots = useSlots()
const popup = shallowRef(null)
const popupContainer = ref(null)

const showPopup = () => {
  if (!popup.value || !props.map || props.coordinates.length !== 2) return

  popup.value.setLngLat(props.coordinates).addTo(props.map)
}

onMounted(() => {
  popup.value = markRaw(new mapboxgl.Popup({
    offset: props.offset,
    anchor: props.anchor,
    closeButton: props.closeButton,
    closeOnClick: props.closeOnClick,
    maxWidth: props.maxWidth,
    minWidth: props.minWidth,
    className: props.className
  }))

  popup.value.on('close', () => {
    emit('close')
  })

  if (popupContainer.value) {
    popup.value.setDOMContent(popupContainer.value)
  }

  if (props.show && props.coordinates.length === 2) {
    showPopup()
  }
})

watch(() => props.show, (newVal) => {
  if (!popup.value || !props.map) return

  if (newVal && props.coordinates.length === 2) {
    showPopup()
  } else {
    popup.value.remove()
  }
}, { immediate: true })

watch(() => props.coordinates, (newVal, oldVal) => {
  if (!popup.value || !props.map) return

  if (newVal.length === 2 &&
      (oldVal.length !== 2 ||
       newVal[0] !== oldVal[0] ||
       newVal[1] !== oldVal[1])) {

    popup.value.setLngLat(newVal)

    if (props.show && !popup.value.isOpen()) {
      popup.value.addTo(props.map)
    }
  }
}, { deep: true })

onBeforeUnmount(() => {
  if (popup.value) {
    popup.value.remove()
    popup.value = null
  }
})
</script>

<template>
  <div>
    <div ref="popupContainer">
      <slot />
    </div>
  </div>
</template>

<style>
.mapboxgl-popup-close-button {
  font-size: 20px;
  width: 26px;
  height: 26px;
  padding: 0;
  right: 2px;
  top: 2px;
  border-radius: 50%;
}

.mapboxgl-popup-close-button span {
  line-height: 23px;
  display: block;
  height: 26px;
}

.mapboxgl-popup-close-button:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.mapboxgl-popup {
  font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";
}

.mapboxgl-popup-content {
  padding: 0;
  border-radius: 8px;
}

/* Dark theme for all mapbox popups */
.dark .mapboxgl-popup-content {
  background-color: hsl(220 5% 14%);
  color: hsl(220 5% 93%);
  border: 1px solid hsl(220 4% 24%);
}

.dark .mapboxgl-popup-close-button {
  color: hsl(220 4% 58%);
}

.dark .mapboxgl-popup-close-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.dark .mapboxgl-popup-anchor-bottom .mapboxgl-popup-tip {
  border-top-color: hsl(220 5% 14%);
}

.dark .mapboxgl-popup-anchor-top .mapboxgl-popup-tip {
  border-bottom-color: hsl(220 5% 14%);
}

.dark .mapboxgl-popup-anchor-left .mapboxgl-popup-tip {
  border-right-color: hsl(220 5% 14%);
}

.dark .mapboxgl-popup-anchor-right .mapboxgl-popup-tip {
  border-left-color: hsl(220 5% 14%);
}
</style>
